import { SW_MESSAGE_SKIP_WAITING } from './serviceWorker/constants'

let registration: ServiceWorkerRegistration | null = null
const updateReadyListeners = new Set<() => void>()

function notifyUpdateReady(): void {
  if (!navigator.serviceWorker.controller) {
    return
  }
  updateReadyListeners.forEach((listener) => listener())
}

function trackInstalling(worker: ServiceWorker): void {
  worker.addEventListener('statechange', () => {
    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
      notifyUpdateReady()
    }
  })
}

function handleRegistration(reg: ServiceWorkerRegistration): void {
  registration = reg

  if (reg.waiting && navigator.serviceWorker.controller) {
    notifyUpdateReady()
  }

  reg.addEventListener('updatefound', () => {
    const installing = reg.installing
    if (installing) {
      trackInstalling(installing)
    }
  })
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) {
    return
  }

  const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(swUrl, { updateViaCache: 'none' })
      .then((reg) => {
        handleRegistration(reg)
      })
      .catch((error: Error) => {
        if (process.env.NODE_ENV === 'production') {
          console.error('[Service Worker] Registration failed:', error)
        } else {
          console.warn('[Service Worker] Registration failed:', error)
        }
      })
  })
}

export function getServiceWorkerRegistration(): ServiceWorkerRegistration | null {
  return registration
}

export function onServiceWorkerUpdateReady(listener: () => void): () => void {
  updateReadyListeners.add(listener)
  return () => {
    updateReadyListeners.delete(listener)
  }
}

export function hasWaitingServiceWorkerUpdate(): boolean {
  return Boolean(registration?.waiting && navigator.serviceWorker.controller)
}

export async function checkForServiceWorkerUpdate(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return
  }

  const reg = registration ?? (await navigator.serviceWorker.getRegistration())
  if (!reg) {
    return
  }

  if (!registration) {
    handleRegistration(reg)
  }

  await reg.update()

  if (reg.waiting && navigator.serviceWorker.controller) {
    notifyUpdateReady()
  }
}

export function applyServiceWorkerUpdate(): boolean {
  const waiting = registration?.waiting
  if (!waiting) {
    return false
  }

  waiting.postMessage({ type: SW_MESSAGE_SKIP_WAITING })
  return true
}
